import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { getSubAdmins, addSubAdmin, updateSubAdmin } from "../api/subAdminApi";
import { showError } from "../utils/alert";

const initialForm = {
  name: "",
  email: "",
  password: "",
  is_verified: false,
};

export default function SubAdminForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadSubAdmin();
    }
  }, [id]);

  const loadSubAdmin = async () => {
    try {
      const res = await getSubAdmins({ page: 1, per_page: 1000, deleted: 0 });
      const admin = (res.data.items || []).find((a) => a.id === parseInt(id, 10));
      if (!admin) {
        showError("Sub admin not found");
        navigate("/sub-admins");
        return;
      }
      setForm({
        name: admin.name || "",
        email: admin.email || "",
        password: "",
        is_verified: !!admin.is_verified,
      });
    } catch (err) {
      showError("Failed to load sub admin");
      navigate("/sub-admins");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showError("Name is required");
    if (!form.email.trim()) return showError("Email is required");
    if (!isEdit && !form.password) return showError("Password is required");

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        is_verified: form.is_verified,
      };
      if (form.password) {
        payload.password = form.password;
      }

      if (isEdit) {
        await updateSubAdmin(id, payload);
      } else {
        await addSubAdmin(payload);
      }
      navigate("/sub-admins");
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      if (err?.response?.status === 409) {
        showError(apiMessage || "Account already exists");
      } else {
        showError(apiMessage || (isEdit ? "Failed to update sub admin" : "Failed to add sub admin"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout title={isEdit ? "Edit Sub Admin" : "Add Sub Admin"}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">
            {isEdit ? "Edit Sub Admin" : "Add New Sub Admin"}
          </h5>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Name *</label>
              <input
                name="name"
                className="form-control"
                value={form.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Email *</label>
              <input
                name="email"
                type="email"
                className="form-control"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">
                Password {isEdit && <span className="text-muted">(leave blank to keep)</span>}
              </label>
              <input
                name="password"
                type="password"
                className="form-control"
                value={form.password}
                onChange={handleChange}
                required={!isEdit}
              />
            </div>
            <div className="form-check mb-3">
              <input
                id="is_verified"
                name="is_verified"
                type="checkbox"
                className="form-check-input"
                checked={form.is_verified}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="is_verified">
                Is Active
              </label>
            </div>
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn skedq-accent-btn"
                disabled={loading}
              >
                {loading ? "Saving..." : isEdit ? "Update" : "Add"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/sub-admins")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

